import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.js";
import LocationFilter from "../locaties/LocationFilter.js";
import "./alltasks.css";

function AllTasks() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("jwtToken");
  const { userInfo, isAuthenticated } = useAuth();
  const userRole = userInfo?.role || "";
  const is_single_user = userInfo?.is_single_user;

  const [tasks, setTasks] = useState([]);
  const [zoekterm, setZoekterm] = useState("");
  const [debouncedZoekterm, setDebouncedZoekterm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });

  axios.defaults.withCredentials = true;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoekterm(zoekterm.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [zoekterm]);

  useEffect(() => {
    if (userRole === "super_admin" || userRole === "locatie_admin") {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/location/allLocations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then((res) => {
          const locs = res.data.data || res.data;
          setLocations(Array.isArray(locs) ? locs : []);
        })
        .catch((err) => {
          console.error("Error fetching locations", err);
          setLocations([]);
        });
    } else {
      setLocations([]);
    }
  }, [userRole, token]);

  const fetchTasks = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/tasks/all`,
        {
          params: {
            page,
            limit,
            search: debouncedZoekterm,
            showInactive,
            location_id: locationFilter
          },
          headers: {
            Authorization: `Bearer ${token}`
          },
        }
      );

      setTasks(response.data.data || []);
      setPagination(
        response.data.pagination || {
          page: 1,
          limit,
          total: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false
        }
      );
    } catch (error) {
      console.error("Error fetching tasks", error);
      setTasks([]);
      setPagination({
        page: 1,
        limit,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, page, limit, debouncedZoekterm, showInactive, locationFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleDelete = async (id) => {
    if (!window.confirm(`Weet je zeker dat je deze taak wilt verwijderen?`)) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/tasks/delete/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });
      fetchTasks();
      alert(`Taak verwijderd.`);
    } catch (err) {
      console.error("Fout bij verwijderen taak", err);
      alert("Verwijderen mislukt.");
    }
  };

  const handleEditTask = (id) => navigate(`/tasks/edit/${id}`);

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const renderPaginationButtons = () => {
    if ((pagination.totalPages || 1) <= 1) return null;

    const currentPage = pagination.page || 1;
    const totalPages = pagination.totalPages || 1;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages, 5);
    }

    if (currentPage >= totalPages - 1) {
      startPage = Math.max(1, totalPages - 4);
    }

    const buttons = [];

    for (let i = startPage; i <= endPage; i += 1) {
      buttons.push(
        <button
          key={i}
          type="button"
          className={`btn btn-sm ${i === currentPage ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  const startRecord = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endRecord =
    pagination.total === 0
      ? 0
      : Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="container-fluid px-3 px-md-5 mt-4">
      {isAuthenticated ? (
        <>
          <div className="row mb-4 align-items-center">
            <div className="col-12 col-md-6 text-center text-md-start mb-2 mb-md-0">
              <h2 className="h4">Overzicht Taken</h2>
            </div>
            <div className="col-12 col-md-6 text-end text-md-center">
              {["super_admin", "locatie_admin"].includes(userRole) && (
                <Link to="/tasks/new" className="btn btn-success">
                  <i className="bi bi-plus-circle me-2"></i>Nieuwe Taak
                </Link>
              )}
            </div>
          </div>

          {!is_single_user && (userRole === "super_admin" || userRole === "locatie_admin") && (
            <div className="mb-3">
              <LocationFilter
                userRole={userRole}
                locationFilter={locationFilter}
                setLocationFilter={(value) => {
                  setLocationFilter(value);
                  setPage(1);
                }}
                locations={locations}
              />
            </div>
          )}

          <div className="row mb-3">
            <div className={`col-12 ${!is_single_user ? 'col-md-6' : 'col-md-7'} mb-2`}>
              <input
                type="text"
                className="form-control rounded-0"
                placeholder="Zoek op titel of beschrijving"
                value={zoekterm}
                onChange={(e) => {
                  setZoekterm(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className={`col-12 ${!is_single_user ? 'col-md-2' : 'col-md-2'} mb-2`}>
              <select
                className="form-select rounded-0"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value={10}>Toon 10</option>
                <option value={20}>Toon 20</option>
                <option value={50}>Toon 50</option>
                <option value={100}>Toon 100</option>
              </select>
            </div>

            <div className={`col-12 ${!is_single_user ? 'col-md-4' : 'col-md-3'} d-flex flex-column flex-md-row justify-content-md-end align-items-md-center gap-3`}>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showInactiveTasks"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setPage(1);
                  }}
                />
                <label className="form-check-label" htmlFor="showInactiveTasks">
                  Toon inactieve taken
                </label>
              </div>

              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setZoekterm("");
                  setShowInactive(false);
                  setLocationFilter("all");
                  setLimit(20);
                  setPage(1);
                }}
              >
                Filters wissen
              </button>
            </div>
          </div>

          <div className="row mb-3 align-items-center">
            <div className="col-12">
              <small className="text-muted">
                Toont {startRecord} t/m {endRecord} van {pagination.total || 0} taken
              </small>
            </div>
          </div>

          {isLoading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className="row">
                {tasks.map((task) => (
                  <div key={task.task_id} className="col-12 col-md-6 col-lg-4 mb-4">
                    <div
                      className={`card h-100 shadow-sm ${task.status === "inactive" ? "border-warning" : ""}`}
                      onClick={() => handleEditTask(task.task_id)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="card-body d-flex flex-column">
                        <h5 className="card-title">{task.name}</h5>

                        <h6 className="card-subtitle text-muted mb-2">
                          Status:{" "}
                          <span className={task.status === "inactive" ? "text-warning" : ""}>
                            {task.status}
                          </span>
                        </h6>

                        <p className="card-text small">{task.description}</p>

                        {task.users && task.users.length > 0 && (
                          <div className="mb-2">
                            <strong>Gekoppeld aan:</strong>
                            <ul className="small mb-0">
                              {task.users.map((user) => (
                                <li key={user.user_id}>{user.name}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <p className="text-muted small mb-2">
                          Aangemaakt: {new Date(task.created_at).toLocaleDateString()}
                        </p>

                        <div
                          className="mt-auto d-flex justify-content-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {["super_admin", "locatie_admin"].includes(userRole) && (
                            <>
                              <Link
                                to={`/tasks/edit/${task.task_id}`}
                                className="btn btn-sm btn-primary px-2 py-1"
                              >
                                Bewerk
                              </Link>
                              <button
                                onClick={() => handleDelete(task.task_id)}
                                className="btn btn-sm btn-danger px-2 py-1"
                              >
                                Verwijder
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {tasks.length === 0 && (
                  <div className="col-12 text-center text-muted">Geen taken gevonden.</div>
                )}
              </div>

              {(pagination.totalPages || 1) > 1 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-3">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasPrevPage}
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                      Vorige
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasNextPage}
                      onClick={() => setPage((prev) => prev + 1)}
                    >
                      Volgende
                    </button>
                  </div>

                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    {renderPaginationButtons()}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <p>Je bent niet geauthenticeerd.</p>
      )}
    </div>
  );
}

export default AllTasks;